import { z } from 'zod';
import { IsoTimestampSchema, SchemaVersionSchema } from './shared.js';
/**
 * V1 customer-facing email template keys per docs/05_BRAND_SYSTEM.md §3.1.
 * 11 keys. Order-flow review (2026-10-28) removed `intake_resend` (it could
 * never fill in the intake link; the Resend intake link action resends
 * `intake_send`) and `msa_review_send` (an agreement-copy email that was never
 * wired and linked to a DocuSign address the customer cannot open). The boot
 * seed retires their stored rows (RETIRED_SYSTEM_TEMPLATE_KEYS).
 *
 * Sorted alphabetically for diff stability.
 */
export const EmailTemplateKeySchema = z.enum([
    // Order-flow review (2026-10-28): the confirmation after an online acceptance.
    'acceptance_confirmation',
    'custom_freeform',
    'discovery_followup',
    'intake_send',
    'invoice_overdue_30d',
    // Order-flow review (2026-10-28): the pay link for a legacy (DocuSign-era) acceptance.
    'invoice_ready',
    'meeting_followup',
    'portal_welcome',
    'proposal_send',
    'renewal_reminder_30d_admin_escalation',
    'renewal_reminder_60d',
    'renewal_reminder_74d',
    'thank_you',
]);
/**
 * T-EMAIL-TEMPLATE-CREATION (Option a) — split the key space.
 *
 * SYSTEM keys are the closed 13-key enum above. They are backed by a `.tsx`
 * transactional renderer (`notifications/templates/*`) and may be edited /
 * version-published but never created or deleted via the admin API.
 *
 * USER keys are admin-created, free-form, **compose-only** templates. They live
 * in the SAME `email_templates` table (template_key is already `text`), but are
 * distinguished by a `user:` key prefix. They have NO transactional renderer —
 * they are only ever selected in the rep compose modal, where their stored body
 * is shown with the existing safe `{{token}}` substitution. The `user:` prefix
 * is the single, auditable discriminator: any consumer can tell at a glance
 * (and the transactional render/send guards can refuse) without a DB-column
 * migration or threading a `kind` field through every read path.
 *
 * A user key is `user:` + a slug of `[a-z0-9-]`, e.g. `user:welcome-pack`.
 */
export const USER_TEMPLATE_KEY_PREFIX = 'user:';
/** True iff `key` is one of the closed, renderer-backed system keys. */
export function isSystemTemplateKey(key) {
    return EmailTemplateKeySchema.safeParse(key).success;
}
/** True iff `key` is an admin-created, compose-only user template key. */
export function isUserTemplateKey(key) {
    return key.startsWith(USER_TEMPLATE_KEY_PREFIX) && key.length > USER_TEMPLATE_KEY_PREFIX.length;
}
/**
 * Slugify a human display name into the slug portion of a user key (without the
 * `user:` prefix). Lowercases, collapses non-alphanumerics to single hyphens,
 * trims leading/trailing hyphens, and caps length. Returns '' for input that
 * has no usable characters (caller must handle / reject).
 */
export function slugifyUserTemplateName(name) {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);
}
/** Compose a full user key from an already-slugified slug. */
export function userTemplateKey(slug) {
    return `${USER_TEMPLATE_KEY_PREFIX}${slug}`;
}
export const EmailTemplateSurfaceSchema = z.enum([
    'account_detail',
    'deal_detail',
    'submission_detail',
    'meeting_detail',
]);
export const EmailTemplateSchema = z.object({
    id: z.string(),
    type: z.literal('email_template'),
    schemaVersion: SchemaVersionSchema,
    templateKey: EmailTemplateKeySchema,
    templateVersion: z.number().int().positive(),
    isCurrent: z.boolean().default(false),
    availableOn: z.array(EmailTemplateSurfaceSchema),
    conditionalDisplay: z
        .object({
        requireDealState: z.array(z.string()).optional(),
        requireRenewalWithinDays: z.number().int().positive().optional(),
    })
        .optional(),
    subject: z.string(),
    bodyHtml: z.string(),
    bodyText: z.string(),
    mergeFields: z.array(z.string()),
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    deprecatedAt: IsoTimestampSchema.optional(),
    version: z.number().int().positive(),
});
/**
 * Detect un-filled template scaffolding in an outgoing email body, so a rep
 * can never ship a half-finished template. Two markers count as "unresolved":
 *
 *   1. Double-brace merge tokens — `{{anything}}` — merge fields that were
 *      never substituted with real data (e.g. `{{intakeLink}}` when no link
 *      could be resolved at compose time).
 *   2. Bracketed instruction stubs — `[Rep summarizes …]`, `[Insert …]`,
 *      `[your name]` — leftover author guidance the rep was meant to replace.
 *
 * The bracket matcher is intentionally conservative: it only trips when the
 * bracket interior STARTS with a known authoring lead-word, so legitimate
 * uses like "[1]" or "[see attached]" don't false-positive. Returns the
 * offending snippets (deduped, in first-seen order); an empty array means the
 * body is clean and safe to send.
 *
 * Shared by the server-side send guard (`POST /api/v1/email/send`) and the
 * client-side compose modal so both surfaces agree on what "unfilled" means.
 */
/**
 * RIG-401 follow-on (Kenny, 2026-09-28: "email templates should be
 * admin-editable copy"). The names of every `{{token}}` in a piece of stored
 * template copy, deduped, in first-seen order. Drives which merge fields the
 * compose endpoint resolves for a given template version, so an admin who
 * adds or removes a token in the editor changes what gets filled.
 */
export function collectPlaceholderNames(text) {
    const names = [];
    const seen = new Set();
    for (const m of text.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)) {
        const name = m[1] ?? '';
        if (name.length > 0 && !seen.has(name)) {
            seen.add(name);
            names.push(name);
        }
    }
    return names;
}
function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/**
 * Optional blocks (RIG-401 follow-on, Kenny 2026-09-28): a stretch of copy
 * that appears only when a field has a value, written as
 *
 *   {{#if coverNote}} … {{/if}}
 *
 * Used by the automated proposal email for the rep's cover note and the
 * pay-online section. Resolved by `fillPlaceholders` BEFORE token
 * substitution: the block is kept (markers removed) when the field has a
 * non-empty value that is not itself an unresolved token, and dropped
 * otherwise. Blocks do not nest.
 */
const OPTIONAL_BLOCK_REGEX = /\{\{#if\s+([\w.]+)\s*\}\}([\s\S]*?)\{\{\/if\}\}/g;
function hasRealValue(raw) {
    if (raw === undefined)
        return false;
    const value = String(raw);
    if (value.trim().length === 0)
        return false;
    return !/^\{\{\s*[\w.]+\s*\}\}$/.test(value);
}
/**
 * Check that every `{{#if field}}` has a matching `{{/if}}` and vice versa.
 * Returns null when the copy is well formed, else a plain-language message
 * suitable for showing an admin.
 */
export function validateOptionalBlocks(text) {
    const opens = (text.match(/\{\{#if\s+[\w.]+\s*\}\}/g) ?? []).length;
    const closes = (text.match(/\{\{\/if\}\}/g) ?? []).length;
    if (opens === closes) {
        // Balanced counts can still interleave badly ({{/if}} before {{#if}}).
        let depth = 0;
        for (const m of text.matchAll(/\{\{#if\s+[\w.]+\s*\}\}|\{\{\/if\}\}/g)) {
            depth += m[0].startsWith('{{#if') ? 1 : -1;
            if (depth < 0)
                return 'An {{/if}} appears before its {{#if …}}.';
            if (depth > 1)
                return 'Optional blocks cannot be nested inside each other.';
        }
        return null;
    }
    return opens > closes
        ? `${opens - closes} optional block(s) are missing their closing {{/if}}.`
        : `${closes - opens} closing {{/if}} marker(s) have no opening {{#if …}}.`;
}
/**
 * Substitute `{{token}}` markers in stored template copy with real values.
 * Optional blocks are resolved first (see above). A token whose value is
 * undefined, or whose value is itself still a token (the compose resolver's
 * "could not resolve" signal), is left in place so the send guard can refuse
 * it. With `escapeHtml`, values are HTML-escaped, which is required when
 * filling `bodyHtml`; `bodyText` and the subject are plain text and take the
 * value verbatim.
 */
export function fillPlaceholders(text, values, opts = {}) {
    const withBlocks = text.replace(OPTIONAL_BLOCK_REGEX, (_whole, name, inner) => hasRealValue(values[name]) ? inner : '');
    return withBlocks.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (whole, name) => {
        const raw = values[name];
        if (raw === undefined)
            return whole;
        const value = String(raw);
        if (/^\{\{\s*[\w.]+\s*\}\}$/.test(value))
            return whole;
        return opts.escapeHtml === true ? escapeHtml(value) : value;
    });
}
function hasToken(text, field) {
    return collectPlaceholderNames(text).includes(field);
}
export function findMissingRequiredTokens(draft, required) {
    const out = [];
    for (const field of required) {
        const missingIn = [];
        if (!hasToken(draft.bodyHtml, field))
            missingIn.push('html');
        if (!hasToken(draft.bodyText, field))
            missingIn.push('text');
        if (missingIn.length > 0)
            out.push({ field, missingIn });
    }
    return out;
}
/** "HTML body" / "plain-text body" / "both bodies" for a marker such as "(missing from HTML body)". */
export function describeMissingIn(missingIn) {
    if (missingIn.length >= 2)
        return 'both bodies';
    return missingIn[0] === 'text' ? 'plain-text body' : 'HTML body';
}
/**
 * One sentence per missing token, naming the body that lost it, joined
 * with a space. Empty string when nothing is missing.
 */
export function formatMissingRequiredTokens(missing) {
    return missing
        .map((m) => {
        const token = `{{${m.field}}}`;
        if (m.missingIn.length >= 2)
            return `Both bodies are missing ${token}.`;
        if (m.missingIn[0] === 'html')
            return `The HTML body is missing ${token}.`;
        return `The plain-text body is missing ${token}.`;
    })
        .join(' ');
}
/**
 * Overnight B2: tokens a SYSTEM email cannot fill. The automated send path
 * computes exactly the template definition's merge fields, so a `{{token}}`
 * outside that set (a typo, or the wrong case such as
 * `{{CUSTOMERPRIMARYCONTACTNAME}}`) would reach the customer unfilled. Every
 * token in the subject and both bodies must be in `allowed` (exact case).
 * Returns the offenders, deduped, in first-seen order.
 */
export function findUnknownTokens(draft, allowed) {
    const allow = new Set(allowed);
    return collectPlaceholderNames(`${draft.subject}\n${draft.bodyHtml}\n${draft.bodyText}`).filter((name) => !allow.has(name));
}
export function formatUnknownTokens(unknown, allowed) {
    const list = unknown.map((t) => `{{${t}}}`);
    const lead = list.length === 1
        ? `${list[0]} is not a token this email can fill.`
        : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]} are not tokens this email can fill.`;
    const available = allowed.length > 0 ? allowed.map((t) => `{{${t}}}`).join(', ') : 'none';
    return `${lead} Tokens available: ${available}.`;
}
export function findUnresolvedPlaceholders(body) {
    const found = [];
    const seen = new Set();
    const push = (raw) => {
        const key = raw.trim();
        if (key.length > 0 && !seen.has(key)) {
            seen.add(key);
            found.push(key);
        }
    };
    // 1. {{ merge token }} — always unresolved.
    for (const m of body.matchAll(/\{\{\s*[\w.]+\s*\}\}/g)) {
        push(m[0]);
    }
    // 2. [Instruction stub] — only when the interior leads with an authoring
    //    cue word, to avoid catching real bracketed prose.
    const STUB_LEAD = /^(rep\b|insert\b|todo\b|tbd\b|sample\b|placeholder\b|optional\b|add\b|enter\b|summari|names?\b|your\b|company\b|customer\b|fills?\b|describe\b|list\b)/i;
    for (const m of body.matchAll(/\[[^\]]*\]/g)) {
        const inner = m[0].slice(1, -1).trim();
        if (STUB_LEAD.test(inner))
            push(m[0]);
    }
    return found;
}
//# sourceMappingURL=email-template.js.map